/**
 * CodeBlockPlugin - Fenced code block autocomplete
 *
 * Detects when the user types `` `` at the start of an empty paragraph
 * (via registerUpdateListener, which fires after character insertion)
 * and shows a floating language prompt.
 *
 * UX flow:
 * 1. User types `` → updateListener detects "``" pattern → floating prompt appears
 * 2. User types language (e.g. "python"), Tab to autocomplete suggestions
 * 3. Press Enter → paragraph with "``" is replaced by a CodeNode with that language
 * 4. Cursor is placed inside the code block, ready to write code
 * 5. Press Escape → prompt dismisses, "``" stays in paragraph
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createParagraphNode, $getNodeByKey, $getSelection, $isRangeSelection } from 'lexical';
import { $isCodeNode } from '@lexical/code';
import { $isCodeBlockDecoratorNode, $createCodeBlockDecoratorNode } from './CodeBlockDecoratorNode';

// ─── Language suggestions ─────────────────────────────────────────────────────

const COMMON_LANGUAGES = [
  'javascript',
  'typescript',
  'python',
  'bash',
  'sh',
  'json',
  'css',
  'html',
  'sql',
  'java',
  'go',
  'rust',
  'php',
  'ruby',
  'swift',
  'kotlin',
  'c',
  'cpp',
  'csharp',
  'yaml',
  'toml',
  'xml',
  'markdown',
  'mermaid',
  'text',
];

// ─── Component ────────────────────────────────────────────────────────────────

export const CodeBlockPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [showPrompt, setShowPrompt] = useState(false);
  const [langValue, setLangValue] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [targetKey, setTargetKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const promptRef = useRef<HTMLDivElement>(null);
  // Ref mirror of showPrompt to use inside Lexical callbacks (avoids stale closure)
  const showPromptRef = useRef(false);

  useEffect(() => {
    showPromptRef.current = showPrompt;
  }, [showPrompt]);

  const suggestions = langValue
    ? COMMON_LANGUAGES.filter(
        (l) => l.startsWith(langValue.toLowerCase()) && l !== langValue.toLowerCase(),
      ).slice(0, 6)
    : [];

  // ─── Create the code block ───────────────────────────────────────────────────

  const confirmCodeBlock = useCallback(
    (language: string) => {
      if (!targetKey) return;
      editor.update(() => {
        const block = $getNodeByKey(targetKey);
        if (!block) return;
        const decoratorNode = $createCodeBlockDecoratorNode(language || 'text', true);
        block.replace(decoratorNode);
        // Always insert an empty paragraph after the code block.
        // DecoratorNode is contentEditable=false — without a subsequent paragraph
        // the editor cursor has nowhere to land after exiting the block.
        const paragraph = $createParagraphNode();
        decoratorNode.insertAfter(paragraph);
        // Move Lexical's internal selection to the new paragraph so it doesn't
        // point to the removed node (which would throw a "selection lost" error).
        // The textarea autoFocus handles actual DOM focus independently.
        paragraph.select();
      });
      // No editor.focus() here — the code block's autoFocus (textarea) handles it.
      setShowPrompt(false);
      setLangValue('');
      setTargetKey(null);
    },
    [editor, targetKey],
  );

  const cancelPrompt = useCallback(() => {
    setShowPrompt(false);
    setLangValue('');
    setTargetKey(null);
    editor.focus();
  }, [editor]);

  // ─── Focus input when prompt opens ──────────────────────────────────────────

  useEffect(() => {
    if (showPrompt) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [showPrompt]);

  // ─── Close on outside click ──────────────────────────────────────────────────

  useEffect(() => {
    if (!showPrompt) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (promptRef.current && !promptRef.current.contains(e.target as Node)) {
        cancelPrompt();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [showPrompt, cancelPrompt]);

  // ─── Detect "``" pattern after each editor update ───────────────────────────

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      // Don't re-trigger if prompt is already visible
      if (showPromptRef.current) return;

      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return;

        const anchorNode = selection.anchor.getNode();
        // Safety: skip if anchor is root
        if (anchorNode.getKey() === 'root') return;

        let parentBlock;
        try {
          parentBlock = anchorNode.getTopLevelElementOrThrow();
        } catch {
          return;
        }

        // Only trigger in a plain paragraph, not inside an existing code block
        if ($isCodeNode(parentBlock) || $isCodeBlockDecoratorNode(parentBlock)) return;

        const textContent = parentBlock.getTextContent();
        // Trigger only when the paragraph contains exactly "``"
        if (textContent !== '``') return;

        // Capture DOM position for the floating prompt
        const nativeSel = window.getSelection();
        let top = 100,
          left = 100;
        if (nativeSel && nativeSel.rangeCount > 0) {
          const rect = nativeSel.getRangeAt(0).getBoundingClientRect();
          top = rect.bottom + 8;
          left = rect.left;
        }

        const key = parentBlock.getKey();
        // Defer React state updates out of Lexical's read context
        setTimeout(() => {
          setPosition({ top, left });
          setTargetKey(key);
          setLangValue('');
          setShowPrompt(true);
        }, 0);
      });
    });
  }, [editor]);

  // ─── Render ───────────────────────────────────────────────────────────────────

  if (!showPrompt) return null;

  return createPortal(
    <div
      ref={promptRef}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        background: 'var(--color-bg-subtle)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: '6px 10px',
        zIndex: 'var(--z-floating)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-sm)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        minWidth: '200px',
        fontSize: 'var(--font-size-sm)',
      }}
    >
      {/* Input row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
        <span
          style={{
            color: 'var(--color-fg-muted)',
            fontFamily: 'var(--font-code)',
            fontSize: 'var(--font-size-md)',
            letterSpacing: 'var(--letter-spacing-tight)',
          }}
        >
          ```
        </span>
        <input
          ref={inputRef}
          value={langValue}
          onChange={(e) => {
            setLangValue(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              confirmCodeBlock(langValue);
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelPrompt();
            }
            if (e.key === 'Tab' && suggestions.length > 0 && suggestions[0]) {
              e.preventDefault();
              setLangValue(suggestions[0]);
            }
          }}
          placeholder="language…"
          style={{
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--color-primary)',
            fontFamily: 'var(--font-code)',
            fontSize: 'inherit',
            flex: 1,
          }}
          spellCheck={false}
          autoComplete="off"
        />
        <span style={{ color: 'var(--color-fg-subtle)', fontSize: 'var(--font-size-xs)' }}>↵</span>
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-xs)' }}>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                confirmCodeBlock(s);
              }}
              style={{
                background: 'var(--color-bg-muted)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                padding: '2px 7px',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-fg-secondary)',
                fontFamily: 'var(--font-code)',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
};
